import { useMemo, useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { ColumnDef } from '@tanstack/react-table';
import { Plus, Pencil, Trash2, Loader2, Zap } from 'lucide-react';
import toast from 'react-hot-toast';
import { TopBanner } from '@/components/TopBanner';
import { DataTable } from '@/components/DataTable';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  useBonusRules,
  useBonusSchedule,
  useCreateBonusRule,
  useUpdateBonusRule,
  useRemoveBonusRule,
  bonusKeys,
} from '@/hooks/useBonus';
import { bonusApi, type BonusRule } from '@/api/bonus.api';

export const Route = createFileRoute('/_authenticated/bonos')({
  component: BonosPage,
});

const schema = z.object({
  name: z.string().min(1, 'Requerido'),
  bonusPercent: z.coerce.number().min(0).max(500),
  minDeposit: z.coerce.number().min(0),
  maxBonus: z.coerce.number().min(0),
  wagerMultiplier: z.coerce.number().min(0),
  isActive: z.enum(['true', 'false']),
});
type FormValues = z.infer<typeof schema>;

const DEFAULTS: FormValues = {
  name: '',
  bonusPercent: 100,
  minDeposit: 0,
  maxBonus: 0,
  wagerMultiplier: 1,
  isActive: 'true',
};

function BonosPage() {
  const { data: rules = [], isLoading } = useBonusRules();
  const { data: schedule } = useBonusSchedule();
  const createMutation = useCreateBonusRule();
  const updateMutation = useUpdateBonusRule();
  const deleteMutation = useRemoveBonusRule();
  const qc = useQueryClient();

  const [isOpen, setIsOpen] = useState(false);
  const [editing, setEditing] = useState<BonusRule | null>(null);
  const [deleting, setDeleting] = useState<BonusRule | null>(null);
  const [forcePercent, setForcePercent] = useState('');

  const modeMutation = useMutation({
    mutationFn: ({ mode, percent }: { mode: 'manual' | 'auto'; percent?: number }) =>
      bonusApi.setMode(mode, percent),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: bonusKeys.schedule() });
      toast.success('Modo actualizado');
    },
    onError: () => toast.error('Error al cambiar modo'),
  });
  const forceMutation = useMutation({
    mutationFn: (percent: number) => bonusApi.forceBonus(percent),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: bonusKeys.schedule() });
      toast.success('Bono forzado');
    },
    onError: () => toast.error('Error al forzar bono'),
  });

  const form = useForm<FormValues>({ resolver: zodResolver(schema), defaultValues: DEFAULTS });

  const onSubmit = async (values: FormValues) => {
    const dto = {
      name: values.name,
      bonusPercent: values.bonusPercent,
      minDeposit: values.minDeposit,
      maxBonus: values.maxBonus,
      wagerMultiplier: values.wagerMultiplier,
      isActive: values.isActive === 'true',
    };
    if (editing) await updateMutation.mutateAsync({ id: editing.id, dto });
    else await createMutation.mutateAsync(dto);
    setIsOpen(false);
    setEditing(null);
    form.reset(DEFAULTS);
  };

  const handleEdit = (r: BonusRule) => {
    setEditing(r);
    form.reset({
      name: r.name,
      bonusPercent: r.bonusPercent,
      minDeposit: Number(r.minDeposit),
      maxBonus: Number(r.maxBonus ?? 0),
      wagerMultiplier: Number(r.wagerMultiplier),
      isActive: r.isActive ? 'true' : 'false',
    });
    setIsOpen(true);
  };

  const handleDelete = async () => {
    if (!deleting) return;
    await deleteMutation.mutateAsync(deleting.id);
    setDeleting(null);
  };

  const columns = useMemo<ColumnDef<BonusRule>[]>(
    () => [
      {
        accessorKey: 'name',
        header: 'Nombre',
        cell: ({ row }) => <div className="font-medium">{row.original.name}</div>,
      },
      {
        accessorKey: 'bonusPercent',
        header: 'Bono',
        cell: ({ row }) => <Badge variant="default">{row.original.bonusPercent}%</Badge>,
      },
      {
        accessorKey: 'minDeposit',
        header: 'Min. depósito',
        cell: ({ row }) => <span className="text-sm">{row.original.minDeposit}</span>,
      },
      {
        accessorKey: 'maxBonus',
        header: 'Máx. bono',
        cell: ({ row }) => <span className="text-sm">{row.original.maxBonus ?? '—'}</span>,
      },
      {
        accessorKey: 'wagerMultiplier',
        header: 'Wager',
        cell: ({ row }) => <span className="text-sm">x{row.original.wagerMultiplier}</span>,
      },
      {
        accessorKey: 'isActive',
        header: 'Estado',
        cell: ({ row }) => (
          <Badge variant={row.original.isActive ? 'default' : 'secondary'}>
            {row.original.isActive ? 'Activa' : 'Inactiva'}
          </Badge>
        ),
      },
      {
        id: 'actions',
        header: '',
        cell: ({ row }) => (
          <div className="flex items-center gap-2">
            <Button size="icon" variant="ghost" onClick={() => handleEdit(row.original)}>
              <Pencil className="h-4 w-4" />
            </Button>
            <Button
              size="icon"
              variant="ghost"
              className="text-red-500"
              onClick={() => setDeleting(row.original)}
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        ),
      },
    ],
    [],
  );

  return (
    <>
      <TopBanner
        title="Bonos"
        subtitle="Reglas de bono + scheduler"
        action={
          <Button
            onClick={() => {
              setEditing(null);
              form.reset(DEFAULTS);
              setIsOpen(true);
            }}
          >
            <Plus className="mr-2 h-4 w-4" />
            Nueva regla
          </Button>
        }
      />
      <div className="flex-1 space-y-6 overflow-auto p-6">
        <Card>
          <CardContent className="flex flex-wrap items-center gap-4 p-4">
            <div>
              <div className="text-xs text-muted-foreground">Modo</div>
              <Badge variant={schedule?.mode === 'auto' ? 'default' : 'secondary'}>
                {schedule?.mode ?? '—'}
              </Badge>
            </div>
            <div>
              <div className="text-xs text-muted-foreground">Ahora</div>
              <div className="text-sm font-medium">
                {schedule?.currentDay ?? '—'} · {schedule?.currentHour ?? '—'}h
              </div>
            </div>
            <div>
              <div className="text-xs text-muted-foreground">Bono default</div>
              <div className="text-sm font-medium">{schedule?.defaultPercent ?? 0}%</div>
            </div>
            <div>
              <div className="text-xs text-muted-foreground">Reglas activas</div>
              <div className="text-sm font-medium">{schedule?.activeRules?.length ?? 0}</div>
            </div>
            <div className="ml-auto flex items-center gap-2">
              <Button
                size="sm"
                variant="outline"
                onClick={() =>
                  modeMutation.mutate({ mode: schedule?.mode === 'auto' ? 'manual' : 'auto' })
                }
                disabled={modeMutation.isPending}
              >
                {schedule?.mode === 'auto' ? 'Pasar a Manual' : 'Pasar a Auto'}
              </Button>
              <Input
                className="w-24"
                type="number"
                placeholder="% forzar"
                value={forcePercent}
                onChange={(e) => setForcePercent(e.target.value)}
              />
              <Button
                size="sm"
                onClick={() => {
                  const p = Number(forcePercent);
                  if (p >= 0) {
                    forceMutation.mutate(p);
                    setForcePercent('');
                  }
                }}
                disabled={forceMutation.isPending || !forcePercent}
              >
                <Zap className="mr-1 h-4 w-4" />
                Forzar
              </Button>
            </div>
          </CardContent>
        </Card>

        <DataTable
          columns={columns}
          data={rules}
          isLoading={isLoading}
          searchPlaceholder="Buscar regla..."
          searchColumn="name"
        />
      </div>

      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editing ? 'Editar regla' : 'Nueva regla'}</DialogTitle>
          </DialogHeader>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Nombre</FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <div className="grid grid-cols-2 gap-3">
                <FormField
                  control={form.control}
                  name="bonusPercent"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Bono %</FormLabel>
                      <FormControl>
                        <Input type="number" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="wagerMultiplier"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Wager (x)</FormLabel>
                      <FormControl>
                        <Input type="number" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="minDeposit"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Min. depósito</FormLabel>
                      <FormControl>
                        <Input type="number" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="maxBonus"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Máx. bono (0=sin tope)</FormLabel>
                      <FormControl>
                        <Input type="number" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              <FormField
                control={form.control}
                name="isActive"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Estado</FormLabel>
                    <Select onValueChange={field.onChange} defaultValue={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="true">Activa</SelectItem>
                        <SelectItem value="false">Inactiva</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <DialogFooter>
                <Button
                  type="submit"
                  disabled={createMutation.isPending || updateMutation.isPending}
                >
                  {(createMutation.isPending || updateMutation.isPending) && (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  )}
                  Guardar
                </Button>
              </DialogFooter>
            </form>
          </Form>
        </DialogContent>
      </Dialog>

      <Dialog open={!!deleting} onOpenChange={() => setDeleting(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>¿Eliminar regla?</DialogTitle>
            <DialogDescription>
              {deleting && (
                <>
                  Se eliminará <strong>{deleting.name}</strong>. Esta acción no se puede deshacer.
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleting(null)}>
              Cancelar
            </Button>
            <Button variant="destructive" onClick={handleDelete} disabled={deleteMutation.isPending}>
              {deleteMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Eliminar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
